// ElectronBridge (renderer side). The preload script exposes `window.stationHost`; printing
// goes through it to the main process (Node `net`). Scanning uses the webcam, as on the web.

import { createCameraScanner } from './camera.js';
import { toBase64 } from './job.js';
import { createPrinter, TRANSPORT_GRACE_MS } from './printer.js';
import { ReprintQueue } from './reprint-queue.js';
import {
  DEVICE_TIMEOUT_MS,
  type Device,
  type DeviceBridge,
  type PrintFailureReason,
  type Printer,
  type PrinterTarget,
  type Scanner,
} from './types.js';

/** Result of a raw TCP send or probe, as the main process reports it. */
export type TcpOutcome = { ok: true } | { ok: false; reason: PrintFailureReason; message: string };

/**
 * What the Electron preload exposes on `window.stationHost` (contextIsolation on, so only
 * these functions cross into the page).
 */
export interface StationHostApi {
  readonly platform: 'electron';
  appVersion(): Promise<string>;
  /** Sends raw bytes (base64) to host:port over TCP, within `timeoutMs`. */
  print(base64: string, host: string, port: number, timeoutMs: number): Promise<TcpOutcome>;
  /** Opens and closes a TCP connection to host:port. */
  status(host: string, port: number, timeoutMs: number): Promise<TcpOutcome>;
  /** OS-backed encryption (Windows DPAPI) for small secrets, e.g. the device refresh secret. */
  secureStorage: {
    isAvailable(): Promise<boolean>;
    /** Returns base64 ciphertext. */
    encrypt(plain: string): Promise<string>;
    decrypt(base64: string): Promise<string>;
  };
  /**
   * Pairing and token requests made by the main process (review CR8B-1): the refresh secret
   * reaches the page only sealed. Same shape as `DeviceAuthHostApi` in @enp-suite/data.
   */
  deviceAuth?: {
    request(
      path: string,
      baseUrl: string,
      body: string,
    ): Promise<{ status: number; body: string } | { network: string }>;
  };
}

declare global {
  interface Window {
    stationHost?: StationHostApi;
  }
}

export interface NetworkBridgeOptions {
  /** The station's printer, read at print time (it can change in settings). */
  getTarget: () => PrinterTarget | undefined;
  timeoutMs?: number;
}

export class ElectronBridge implements DeviceBridge {
  readonly kind = 'electron' as const;
  readonly reprints = new ReprintQueue();
  readonly scanner: Scanner = createCameraScanner();
  readonly printer: Printer;

  constructor(host: StationHostApi, { getTarget, timeoutMs }: NetworkBridgeOptions) {
    // The main process applies `ms` to the socket; the JS guard waits a little longer so the
    // main process's own result (sent, or timeout) normally wins.
    const ms = timeoutMs ?? DEVICE_TIMEOUT_MS;
    this.printer = createPrinter({
      queue: this.reprints,
      timeoutMs: ms,
      graceMs: TRANSPORT_GRACE_MS,
      transport: {
        kind: 'network',
        send: async (job) => {
          const target = getTarget();
          if (!target) return { ok: false, reason: 'not-configured' };
          return host.print(toBase64(job.zpl), target.host, target.port, ms);
        },
        probe: async () => {
          const target = getTarget();
          if (!target) return { state: 'not-configured' };
          const outcome = await host.status(target.host, target.port, ms);
          const address = `${target.host}:${target.port}`;
          return outcome.ok
            ? { state: 'ready', target: address }
            : { state: 'offline', target: address, message: outcome.message };
        },
      },
    });
  }

  async discover(): Promise<Device[]> {
    // LAN discovery arrives with printer setup (M3). Printers are entered by address for now.
    return [];
  }
}
